/* eslint-disable @typescript-eslint/no-unused-vars */
import styled from '@emotion/styled'
import {
  EBoxEventSaveNameAction,
  ESessionPhase,
  HAND_COMBINATIONS_LABELS,
  TBoxBodyPublic,
  TGamePublic,
  TSessionBodyPublic,
} from '@lbs/shared-rp-types'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import eventBus, { getBoxEventName } from './utils/eventBus'

// Translation map for box events
const eventTranslations: Record<EBoxEventSaveNameAction, string> = {
  [EBoxEventSaveNameAction.ANTE_PLACED]: 'Ставка ANTE',
  [EBoxEventSaveNameAction.BONUS_PLACED]: 'Ставка BONUS',
  [EBoxEventSaveNameAction.BET_PLACED]: 'Ставка BET',
  [EBoxEventSaveNameAction.INSURANCE_PLACED]: 'Страховка поставлена',
  [EBoxEventSaveNameAction.ROLLBACK_LAST_STAKE]: 'Отмена последней ставки',
  [EBoxEventSaveNameAction.ROLLBACK_ALL_STAKES]: 'Отмена всех ставок',
  [EBoxEventSaveNameAction.BONUS_WON]: 'Бонус выигран',
  [EBoxEventSaveNameAction.BONUS_LOST]: 'Бонус проигран',
  [EBoxEventSaveNameAction.INSURANCE_WON]: 'Страховка выиграна',
  [EBoxEventSaveNameAction.INSURANCE_LOST]: 'Страховка проиграна',
  [EBoxEventSaveNameAction.CARD_RECEIVED]: 'Получена карта',
  [EBoxEventSaveNameAction.BOUGHT_SIXTH_CARD]: 'Запрос: шестая карта',
  [EBoxEventSaveNameAction.BOUGHT_SWITCH_CARDS]: 'Запрос: замена карт',
  [EBoxEventSaveNameAction.DECISION_TIME]: 'Бокс принимает решение',
  [EBoxEventSaveNameAction.PASS]: 'Пас',
  [EBoxEventSaveNameAction.LOSE]: 'Проигрыш',
  [EBoxEventSaveNameAction.WIN]: 'Выигрыш',
  [EBoxEventSaveNameAction.TIE]: 'Ничья',
  [EBoxEventSaveNameAction.OFFER_DEALER_GAME]: 'Предложение купить игру у дилера',
  [EBoxEventSaveNameAction.REJECT_BUY_GAME]: 'Отказ от покупки игры',
  [EBoxEventSaveNameAction.CONFIRM_BUY_GAME]: 'Подтверждение покупки игры',
  [EBoxEventSaveNameAction.BOX_RESOLVED]: 'Бокс разрешен',
  [EBoxEventSaveNameAction.BOX_SELECTED]: '',
  [EBoxEventSaveNameAction.NO_GAME]: 'Нет игры',
  [EBoxEventSaveNameAction.INSURANCE_REFUND]: '',
  [EBoxEventSaveNameAction.SETTLE_SKIP_NO_PLAYER_OR_GAME]: '',
  [EBoxEventSaveNameAction.SETTLE_TRANSFER_FAILED]: '',
  [EBoxEventSaveNameAction.SETTLE_TRANSFER_SUCCESS]: '',
  [EBoxEventSaveNameAction.INITIALIZE]: 'Инициализация бокса',
  [EBoxEventSaveNameAction.ERROR]: 'Ошибка',
}

const resultTranslations: Record<TBoxBodyPublic['result']['outcome'], string> = {
  none: 'Без результата',
  pass: 'Пас',
  win: 'Выиграл',
  lose: 'Проиграл',
  tie: 'Ничья',
  noGame: 'Забрал анте',
}

const Wrapper = styled.div`
  container-type: inline-size;
  padding-right: 20px;
  display: flex;
  width: 100%;
`
const EventNotification = styled.div<{ visible: boolean }>`
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
  z-index: 10;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  pointer-events: none;
  font-size: clamp(12px, 8cqw, 40px);
  font-style: normal;
  font-weight: 400;
  line-height: 1;
`

const StaticEventNotification = styled.div`
  z-index: 10;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  pointer-events: none;
  font-size: clamp(12px, 8cqw, 40px);
  font-style: normal;
  font-weight: 400;
  line-height: 1;
  opacity: 1;
`

const BoxEvents: FC<{
  game: TGamePublic
  box: TBoxBodyPublic
  session: TSessionBodyPublic
}> = observer(({ box, game, session }) => {
  const { gameId, sessionId } = game
  const { seatNumber, pendingCardsRequest, hand, decision, result } = box
  const { phase } = session
  const [event, setEvent] = useState<EBoxEventSaveNameAction | null>(null)
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const showEvent = useCallback((action: EBoxEventSaveNameAction) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    setEvent(action)
    setVisible(true)

    timerRef.current = setTimeout(() => {
      setVisible(false)
      setTimeout(() => {
        setEvent(null)
      }, 500)
    }, 3000)
  }, [])

  const handleUpdate = useCallback(
    (action: EBoxEventSaveNameAction) => {
      const skipEvents = [
        EBoxEventSaveNameAction.DECISION_TIME,
        EBoxEventSaveNameAction.BOUGHT_SIXTH_CARD,
        EBoxEventSaveNameAction.BOUGHT_SWITCH_CARDS,
        EBoxEventSaveNameAction.ERROR,
      ]
      if (skipEvents.includes(action)) return

      showEvent(action)
    },
    [showEvent]
  )

  useEffect(() => {
    const keyEvent = getBoxEventName({ gameId, sessionId, seatNumber })

    eventBus.on(keyEvent, handleUpdate)

    return () => {
      eventBus.off(keyEvent, handleUpdate)

      // Clear any existing timer on unmount
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [gameId, seatNumber, sessionId, handleUpdate])

  const isDecision = phase === ESessionPhase.DECISIONS
  const isSettlement = phase === ESessionPhase.SETTLEMENT
  const isFinished = phase === ESessionPhase.FINISHED
  // Only one event can be displayed at a time
  let staticEvent: string | null = null

  if (pendingCardsRequest === 'switch' && isDecision) {
    staticEvent = 'Замена карт'
  } else if (pendingCardsRequest === 'buySix' && isDecision) {
    staticEvent = 'Покупка 6 карты'
  } else if (decision === 'pass') {
    staticEvent = 'Пас'
  } else if (hand && hand.handName && isSettlement && result.outcome !== 'none') {
    staticEvent = `${HAND_COMBINATIONS_LABELS.ru[hand.handName]}:${resultTranslations[result.outcome]}`
  } else if (isSettlement && box.buyDealerGame.cost) {
    staticEvent = 'Покупка игры'
  } else if (isFinished) {
    if (result.outcome === 'win') {
      staticEvent = 'Бокс выиграл'
    } else if (result.outcome === 'lose') {
      staticEvent = 'Бокс проиграл'
    } else if (result.outcome === 'tie') {
      staticEvent = 'Ничья'
    } else if (result.outcome === 'pass') {
      staticEvent = 'Пас'
    } else if (result.outcome === 'noGame') {
      staticEvent = 'Забрал анте'
    }
  }

  // Clear dynamic events when static event appears
  useEffect(() => {
    if (staticEvent) {
      setVisible(false)
      setEvent(null)
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [staticEvent])

  return (
    <Wrapper>
      {staticEvent ? (
        <StaticEventNotification>{staticEvent}</StaticEventNotification>
      ) : (
        <EventNotification visible={visible}>{event && eventTranslations[event]}</EventNotification>
      )}
    </Wrapper>
  )
})

export default BoxEvents
